import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import TenantsTable from "../components/TenantsTable";
import LeaseWaterfall from "../components/LeaseWaterfall";
import InvestModal from "../components/InvestModal";
import KycWizard from "../components/KycWizard";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  TrendingUp,
  Building2,
  ShieldCheck,
  Sparkles,
  BadgeCheck,
} from "lucide-react";
import { api } from "../lib/api";
import { getUser, saveUser } from "../lib/auth";
import { getPropertyDetail } from "../lib/propertyDetails";
import { gbp, pct } from "../lib/format";
import { isKycCompleted } from "../lib/kyc";

const CALC_MIN = 5000;
const CALC_MAX = 100000;
const CALC_STEP = 500;

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(15000);
  const [showKyc, setShowKyc] = useState(false);
  const [showInvest, setShowInvest] = useState(false);
  const [walletTick, setWalletTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch(() => {
        toast.error("Property not found");
        navigate("/marketplace", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const user = getUser();
  const detail = useMemo(() => getPropertyDetail(id), [id]);

  const projected = useMemo(() => {
    if (!property) return { monthly: 0, annual: 0, tokens: 0 };
    const yieldPct = detail.target_yield_pct ?? property.projected_yield_pct;
    const annual = amount * (yieldPct / 100);
    return { annual, monthly: annual / 12, tokens: amount };
  }, [amount, property, detail]);

  const handleInvestClick = () => {
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    if (!isKycCompleted()) {
      setShowKyc(true);
    } else {
      setShowInvest(true);
    }
  };

  const refreshWallet = async () => {
    const u = getUser();
    if (!u) return;
    const p = await api.get(`/portfolio/${u.id}`).then((r) => r.data);
    saveUser({ ...u, wallet_gbp: p.metrics.wallet_gbp });
    setWalletTick((t) => t + 1);
  };

  if (loading || !property) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 bg-slate-200" />
            <div className="h-96 rounded-lg bg-white border border-slate-200" />
          </div>
        </div>
      </div>
    );
  }

  const wallet = getUser()?.wallet_gbp ?? 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="property-detail-page">
      <Header />

      {/* Back button */}
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/marketplace")}
          className="text-slate-600 hover:text-slate-900"
          data-testid="back-to-marketplace-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Marketplace
        </Button>
      </div>

      {/* Hero */}
      <section className="mx-auto mt-4 max-w-7xl px-4 sm:px-6 lg:px-8" data-testid="property-hero">
        <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-900">
          <div className="relative h-[420px] w-full">
            <img
              src={detail.hero_image}
              alt={property.name}
              className="absolute inset-0 h-full w-full object-cover opacity-90"
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.35) 45%, rgba(15,23,42,0.8) 100%)" }}
            />
            <div className="relative z-10 flex h-full flex-col justify-between p-10">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="rounded-sm bg-white/95 px-2.5 py-1 text-xs font-semibold uppercase tracking-widest text-slate-800">
                    <Building2 className="mr-1 inline h-3 w-3 brickstok-gold" /> {property.asset_type}
                  </span>
                  <span className="rounded-sm bg-slate-900/70 px-2.5 py-1 text-xs font-semibold uppercase tracking-widest text-white border border-white/10">
                    {property.city}
                  </span>
                </div>
                <div
                  className="rounded-full px-5 py-2 font-semibold shadow-lg backdrop-blur"
                  style={{
                    background: "linear-gradient(135deg,#D4AF37,#B8941F)",
                    color: "#0F172A",
                    fontFamily: "Playfair Display, serif",
                  }}
                  data-testid="target-yield-badge"
                >
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    <span className="font-mono-num text-lg">{pct(detail.target_yield_pct)}</span>
                    <span className="text-xs font-semibold uppercase tracking-widest">Target Yield</span>
                  </div>
                </div>
              </div>

              <div className="max-w-3xl">
                <div className="overline" style={{ color: "#D4AF37" }}>Featured Asset</div>
                <h1 className="mt-2 font-display text-4xl font-semibold leading-tight tracking-tight text-white sm:text-5xl" data-testid="property-detail-name">
                  {property.name}
                </h1>
                <div className="mt-3 flex items-center gap-2 text-sm text-slate-200">
                  <MapPin className="h-4 w-4 brickstok-gold" /> {property.address}
                </div>

                <div className="mt-8 grid max-w-3xl grid-cols-3 gap-6 border-t border-white/10 pt-6">
                  <div>
                    <div className="overline" style={{ color: "#94a3b8" }}>Valuation</div>
                    <div className="mt-1 font-display text-2xl font-semibold text-white font-mono-num" data-testid="detail-valuation">
                      {gbp(property.valuation_gbp)}
                    </div>
                  </div>
                  <div>
                    <div className="overline" style={{ color: "#94a3b8" }}>Min Ticket</div>
                    <div className="mt-1 font-display text-2xl font-semibold text-white font-mono-num">{gbp(property.min_investment_gbp)}</div>
                  </div>
                  <div>
                    <div className="overline" style={{ color: "#94a3b8" }}>Anchor Tenant</div>
                    <div className="mt-1 font-display text-xl font-semibold text-white">{detail.tenants[0]?.name}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content grid */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            {/* Highlights */}
            <div className="rounded-lg border border-slate-200 bg-white p-8" data-testid="property-highlights">
              <div className="overline">Investment Thesis</div>
              <h2 className="mt-1 font-display text-2xl font-semibold tracking-tight text-slate-900">Why this asset</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{property.description}</p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {detail.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <BadgeCheck className="mt-0.5 h-4 w-4 brickstok-gold" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            <TenantsTable tenants={detail.tenants} />
            <LeaseWaterfall waterfall={detail.waterfall} />
          </div>

          {/* Sticky calculator */}
          <aside className="lg:col-span-1" data-testid="detail-calculator">
            <div className="sticky top-24 space-y-4">
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <div className="border-b border-slate-100 bg-slate-900 px-6 py-5 text-white">
                  <div className="flex items-center gap-2 overline" style={{ color: "#D4AF37" }}>
                    <Sparkles className="h-3 w-3" /> Yield Calculator
                  </div>
                  <div className="mt-2 font-display text-xl font-semibold tracking-tight">
                    Model your allocation
                  </div>
                </div>

                <div className="space-y-6 p-6">
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Investment amount</span>
                      <span className="text-xs text-slate-500 font-mono-num">Min {gbp(CALC_MIN)}</span>
                    </div>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono-num">£</span>
                      <Input
                        type="number"
                        min={CALC_MIN}
                        max={CALC_MAX}
                        step={CALC_STEP}
                        value={amount}
                        onChange={(e) => setAmount(Math.max(CALC_MIN, Math.min(CALC_MAX, Number(e.target.value) || 0)))}
                        className="h-11 pl-7 font-mono-num text-lg font-semibold text-slate-900"
                        data-testid="calc-amount-input"
                      />
                    </div>
                    <div className="mt-4">
                      <Slider
                        value={[amount]}
                        onValueChange={(v) => setAmount(v[0])}
                        min={CALC_MIN}
                        max={CALC_MAX}
                        step={CALC_STEP}
                        data-testid="calc-amount-slider"
                      />
                      <div className="mt-2 flex justify-between text-xs text-slate-500 font-mono-num">
                        <span>{gbp(CALC_MIN)}</span>
                        <span>{gbp(CALC_MAX)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                      <div className="overline">Monthly Payout</div>
                      <div
                        className="mt-1 font-display text-2xl font-semibold font-mono-num"
                        style={{ color: "#D4AF37" }}
                        data-testid="calc-monthly"
                      >
                        {gbp(projected.monthly, { decimals: 2 })}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                      <div className="overline">Annual Yield</div>
                      <div className="mt-1 font-display text-2xl font-semibold text-slate-900 font-mono-num" data-testid="calc-annual">
                        {gbp(projected.annual, { decimals: 0 })}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-sm text-slate-600">Tokens issued (£1 / token)</div>
                    <div className="font-display text-lg font-semibold text-slate-900 font-mono-num" data-testid="calc-tokens">
                      {Math.round(projected.tokens).toLocaleString()} BSTK
                    </div>
                  </div>

                  <Button
                    onClick={handleInvestClick}
                    className="h-12 w-full text-slate-900 hover:brightness-95"
                    style={{ background: "#D4AF37" }}
                    data-testid="detail-invest-btn"
                  >
                    Proceed to Invest
                  </Button>

                  <div className="flex items-start gap-2 text-xs text-slate-500">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 brickstok-gold" />
                    <span>Fractional interests held in a ring-fenced SPV. Distributions in stablecoin.</span>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4 text-xs text-slate-500">
                <span className="font-semibold text-slate-800">Wallet:</span>{" "}
                <span className="font-mono-num" key={walletTick}>{gbp(wallet)}</span>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <InvestModal
        property={property}
        open={showInvest}
        onOpenChange={setShowInvest}
        wallet={wallet}
        onSuccess={refreshWallet}
      />
      <KycWizard
        open={showKyc}
        onOpenChange={setShowKyc}
        onComplete={() => setShowInvest(true)}
      />

      <Footer />
    </div>
  );
}
